import { useState, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { cn } from "@/shared/lib/cn";

type Align = "left" | "center" | "right";

export function InfoTip({ children, align = "center", className }: { children: ReactNode; align?: Align; className?: string }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  function show() {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const top = r.bottom + 6 + window.scrollY;
    let left: number;
    if (align === "center") left = r.left + r.width / 2;
    else if (align === "left") left = r.left;
    else left = r.right;
    setPos({ top, left });
    setOpen(true);
  }

  const transformMap: Record<Align, string> = {
    center: "translateX(-50%)",
    left: "none",
    right: "translateX(-100%)",
  };

  return (
    <span className={cn("relative inline-flex", className)}>
      <button
        ref={btnRef}
        type="button"
        aria-label="Подробнее"
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onFocus={show}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-ink-40 hover:text-ink-60"
      >
        <Info size={14} />
      </button>
      {open && createPortal(
        <span
          role="tooltip"
          style={{
            position: "fixed",
            top: pos.top - window.scrollY,
            left: pos.left,
            transform: transformMap[align],
            zIndex: 9999,
          }}
          className="w-64 px-3 py-2 rounded-md bg-ink text-white text-[12px] leading-snug shadow-md pointer-events-none"
        >
          {children}
        </span>,
        document.body,
      )}
    </span>
  );
}
